import { SITE } from './yawasCopy';

export type LegalSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type LegalPageContent = {
  slug: 'impressum' | 'datenschutz' | 'agb';
  title: string;
  metaTitle: string;
  metaDescription: string;
  updatedLabel: string;
  sections: LegalSection[];
};

const LEGAL_ENTITY_BLOCK = [
  SITE.legal.ownerName,
  `Handelsname: ${SITE.legal.tradeName}`,
  `Markenauftritt: ${SITE.name}`,
  ...SITE.legal.addressLines,
  `UID: ${SITE.legal.uid}`,
].join('\n');

const LEGAL_CONTACT_LINES = [
  `E-Mail: ${SITE.email}`,
  `Telefon Restaurant: ${SITE.phoneDisplay}`,
  `Telefon: ${SITE.legal.phoneDisplay}`,
].join('\n');

export const LEGAL_PAGES: Record<LegalPageContent['slug'], LegalPageContent> = {
  impressum: {
    slug: 'impressum',
    title: 'Impressum',
    metaTitle: `Impressum | ${SITE.name} St. Gallen`,
    metaDescription: `Impressum und Anbieterkennzeichnung von ${SITE.name} (${SITE.legal.tradeName}), ${SITE.legal.addressOneLine}.`,
    updatedLabel: 'Stand: August 2026',
    sections: [
      {
        heading: 'Angaben gemäss Schweizer Recht',
        paragraphs: [
          'Dieses Impressum gilt für die Website sowie die geschäftliche Tätigkeit von:',
          LEGAL_ENTITY_BLOCK,
        ],
      },
      {
        heading: 'Betriebsadresse',
        paragraphs: [
          'Restaurant / Take-away:',
          [SITE.name, ...SITE.addressLines].join('\n'),
        ],
      },
      {
        heading: 'Kontakt',
        paragraphs: [
          LEGAL_CONTACT_LINES,
          'Für Anfragen erreichen Sie uns telefonisch, per E-Mail oder vor Ort während unserer Öffnungszeiten.',
        ],
      },
      {
        heading: 'Tätigkeit',
        paragraphs: [
          `${SITE.name} (${SITE.legal.tradeName}) betreibt ein Restaurant bzw. Take-away in St. Gallen mit Fokus auf hausgemachte Smash Burger, Kebab und weitere Speisen. Bestellungen können vor Ort, telefonisch oder über externe Lieferplattformen (z. B. Just Eat) erfolgen.`,
        ],
      },
      {
        heading: 'Haftungsausschluss',
        paragraphs: [
          'Die Inhalte dieser Website wurden mit grösster Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.',
          'Verbindliche Angaben zu Preisen und Verfügbarkeit entnehmen Sie bitte der aktuellen Speisekarte vor Ort bzw. der jeweiligen Bestellplattform.',
        ],
      },
      {
        heading: 'Urheberrecht',
        paragraphs: [
          `Die auf dieser Website veröffentlichten Inhalte und Werke unterliegen dem schweizerischen Urheberrecht. Jede Vervielfältigung, Bearbeitung, Verbreitung oder Verwertung ausserhalb der Grenzen des Urheberrechts bedarf der vorherigen schriftlichen Zustimmung von ${SITE.legal.ownerName} / ${SITE.name}.`,
        ],
      },
      {
        heading: 'Externe Links',
        paragraphs: [
          'Diese Website kann Links zu externen Websites Dritter enthalten (z. B. Just Eat, Google Maps, soziale Netzwerke). Auf deren Inhalte haben wir keinen Einfluss. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber verantwortlich.',
        ],
      },
    ],
  },

  datenschutz: {
    slug: 'datenschutz',
    title: 'Datenschutzerklärung',
    metaTitle: `Datenschutz | ${SITE.name} St. Gallen`,
    metaDescription: `Datenschutzerklärung von ${SITE.name}: Informationen zur Bearbeitung personenbezogener Daten gemäss Schweizer DSG.`,
    updatedLabel: 'Stand: August 2026',
    sections: [
      {
        heading: '1. Verantwortliche Stelle',
        paragraphs: [
          'Verantwortlich für die Bearbeitung personenbezogener Daten im Zusammenhang mit dieser Website ist:',
          LEGAL_ENTITY_BLOCK,
          LEGAL_CONTACT_LINES,
        ],
      },
      {
        heading: '2. Geltungsbereich',
        paragraphs: [
          'Diese Datenschutzerklärung informiert Sie darüber, wie wir personenbezogene Daten bearbeiten, wenn Sie unsere Website besuchen, uns kontaktieren oder unsere Dienstleistungen in Anspruch nehmen. Grundlage ist das schweizerische Bundesgesetz über den Datenschutz (DSG).',
        ],
      },
      {
        heading: '3. Welche Daten wir bearbeiten',
        paragraphs: ['Je nach Nutzung können folgende Daten bearbeitet werden:'],
        bullets: [
          'Technische Zugriffsdaten (z. B. IP-Adresse, Browser-Typ, Datum und Uhrzeit des Zugriffs, aufgerufene Seiten), soweit diese durch den Hosting-Anbieter oder technische Systeme anfallen',
          'Kontaktdaten, die Sie uns freiwillig mitteilen (z. B. Name, Telefonnummer, E-Mail-Adresse, Anliegen bei Anrufen oder Bestellungen)',
          'Bestell- und Lieferangaben, sofern Sie vor Ort, telefonisch oder über eine externe Plattform bestellen',
        ],
      },
      {
        heading: '4. Zweck der Bearbeitung',
        paragraphs: ['Wir bearbeiten personenbezogene Daten insbesondere zu folgenden Zwecken:'],
        bullets: [
          'Bereitstellung und Betrieb dieser Website',
          'Beantwortung von Anfragen und Kundenkommunikation',
          'Abwicklung von Bestellungen und Reservierungen',
          'Sicherstellung der IT-Sicherheit und Missbrauchsprävention',
          'Erfüllung gesetzlicher Aufbewahrungs- und Nachweispflichten',
        ],
      },
      {
        heading: '5. Rechtsgrundlagen',
        paragraphs: [
          'Die Bearbeitung erfolgt gestützt auf Ihre Einwilligung, zur Erfüllung eines Vertrags bzw. vorvertraglicher Massnahmen, aufgrund überwiegender Interessen (z. B. sicherer Betrieb der Website) oder aufgrund gesetzlicher Pflichten.',
        ],
      },
      {
        heading: '6. Hosting und technische Dienstleister',
        paragraphs: [
          'Zum Betrieb der Website setzen wir technische Dienstleister ein (z. B. Hosting, Content-Delivery, Datenbankdienste). Dabei können Zugriffs- und Logdaten in deren Systemen anfallen. Die Bearbeitung erfolgt nur soweit für den Betrieb erforderlich und im Rahmen der mit den Anbietern vereinbarten Auftragsverhältnisse.',
        ],
      },
      {
        heading: '7. Externe Dienste und Links',
        paragraphs: [
          'Auf unserer Website können Inhalte oder Links zu Drittanbietern eingebunden sein, insbesondere:',
        ],
        bullets: [
          'Karten- und Standortanzeigen (z. B. Google Maps)',
          'Bestellplattformen (z. B. Just Eat)',
          'Soziale Netzwerke (z. B. Instagram, Facebook)',
        ],
      },
      {
        heading: '7.1 Hinweis zu Drittanbietern',
        paragraphs: [
          'Wenn Sie solche Dienste nutzen oder den entsprechenden Links folgen, gelten die Datenschutzbestimmungen der jeweiligen Anbieter. Wir empfehlen, deren Hinweise gesondert zur Kenntnis zu nehmen.',
        ],
      },
      {
        heading: '8. Cookies und ähnliche Technologien',
        paragraphs: [
          'Unsere Website kann technisch notwendige Cookies oder vergleichbare Speichertechnologien verwenden, die für den Betrieb und die Darstellung erforderlich sind. Soweit wir optionale Analyse- oder Marketing-Cookies einsetzen würden, informieren wir Sie darüber und holen – soweit erforderlich – Ihre Einwilligung ein.',
          'Sie können Cookies in den Einstellungen Ihres Browsers grundsätzlich deaktivieren oder löschen. Dadurch können einzelne Funktionen der Website eingeschränkt sein.',
        ],
      },
      {
        heading: '9. Speicherdauer',
        paragraphs: [
          'Wir speichern personenbezogene Daten nur so lange, wie es für die genannten Zwecke erforderlich ist oder gesetzliche Aufbewahrungsfristen bestehen. Danach werden die Daten gelöscht oder anonymisiert.',
        ],
      },
      {
        heading: '10. Weitergabe an Dritte',
        paragraphs: [
          'Eine Weitergabe personenbezogener Daten erfolgt nur, wenn dies zur Vertragserfüllung erforderlich ist, eine gesetzliche Pflicht besteht, Sie eingewilligt haben oder wir ein überwiegendes Interesse daran haben und Ihre Rechte nicht überwiegen. Bestellungen über externe Plattformen werden direkt bei dem jeweiligen Anbieter abgewickelt.',
        ],
      },
      {
        heading: '11. Datensicherheit',
        paragraphs: [
          'Wir treffen angemessene technische und organisatorische Massnahmen, um personenbezogene Daten vor Verlust, Missbrauch und unbefugtem Zugriff zu schützen. Eine absolute Sicherheit bei der Übertragung über das Internet kann jedoch nicht gewährleistet werden.',
        ],
      },
      {
        heading: '12. Ihre Rechte',
        paragraphs: [
          'Nach Massgabe des anwendbaren Datenschutzrechts stehen Ihnen insbesondere folgende Rechte zu:',
        ],
        bullets: [
          'Auskunft über die bearbeiteten Personendaten',
          'Berichtigung unrichtiger Daten',
          'Löschung oder Einschränkung der Bearbeitung',
          'Widerspruch gegen bestimmte Bearbeitungen',
          'Herausgabe bzw. Übertragung von Daten, soweit anwendbar',
        ],
      },
      {
        heading: '12.1 Kontakt für Datenschutzanfragen',
        paragraphs: [
          `Zur Ausübung Ihrer Rechte kontaktieren Sie uns bitte per E-Mail an ${SITE.email}, telefonisch unter ${SITE.phoneDisplay} oder schriftlich an unsere Adresse.`,
        ],
      },
      {
        heading: '13. Änderungen',
        paragraphs: [
          'Wir können diese Datenschutzerklärung bei Bedarf anpassen, insbesondere bei Änderungen der Rechtslage oder unserer Angebote. Die aktuelle Version ist jeweils auf dieser Seite ersichtlich.',
        ],
      },
    ],
  },

  agb: {
    slug: 'agb',
    title: 'Allgemeine Geschäftsbedingungen (AGB)',
    metaTitle: `AGB | ${SITE.name} St. Gallen`,
    metaDescription: `Allgemeine Geschäftsbedingungen von ${SITE.name} für Speisen, Bestellungen und die Nutzung der Website.`,
    updatedLabel: 'Stand: August 2026',
    sections: [
      {
        heading: '1. Geltungsbereich',
        paragraphs: [
          `Diese Allgemeinen Geschäftsbedingungen gelten für die Nutzung der Website von ${SITE.name} sowie für den Kauf von Speisen und Getränken vor Ort, zur Mitnahme und – soweit angeboten – per Telefon oder über externe Bestellplattformen.`,
          'Abweichende Bedingungen Dritter gelten nur, soweit wir ihnen ausdrücklich schriftlich zugestimmt haben.',
        ],
      },
      {
        heading: '2. Anbieter',
        paragraphs: [LEGAL_ENTITY_BLOCK, LEGAL_CONTACT_LINES],
      },
      {
        heading: '3. Angebot und Vertragsschluss',
        paragraphs: [
          'Die Darstellung von Speisen, Preisen und Informationen auf der Website stellt kein verbindliches Angebot dar, sondern eine unverbindliche Präsentation.',
          'Ein Vertrag kommt zustande, wenn wir Ihre Bestellung annehmen – insbesondere durch Bestätigung, Zubereitung oder Ausgabe der Speisen. Bei Bestellungen über Drittplattformen (z. B. Just Eat) gelten zusätzlich deren Nutzungs- und Vertragsbedingungen.',
        ],
      },
      {
        heading: '4. Preise und Zahlung',
        paragraphs: [
          'Es gelten die zum Zeitpunkt der Bestellung aktuellen Preise in Schweizer Franken (CHF). Preisangaben können sich ändern; massgeblich ist die aktuelle Speisekarte vor Ort bzw. die Anzeige auf der jeweiligen Bestellplattform.',
          'Die Zahlung erfolgt nach den vor Ort angebotenen Zahlungsmitteln bzw. gemäss den Vorgaben der genutzten Bestellplattform.',
        ],
      },
      {
        heading: '5. Allergene und Produkthinweise',
        paragraphs: [
          'Trotz sorgfältiger Zubereitung können Spuren von Allergenen nicht vollständig ausgeschlossen werden. Bei Unverträglichkeiten oder Allergien fragen Sie bitte unser Personal vor der Bestellung.',
          'Abbildungen auf der Website sind beispielhaft und können vom tatsächlichen Produkt abweichen.',
        ],
      },
      {
        heading: '6. Abholung, Verzehr vor Ort und Lieferung',
        paragraphs: [
          'Abhol- und Wartezeiten können je nach Auslastung variieren. Bei Lieferungen über externe Partner sind Lieferzeiten und -bedingungen Sache des jeweiligen Partners.',
          'Für verspätete Abholung oder unsachgemässe Lagerung nach Übergabe übernehmen wir keine Haftung für Qualitätsverlust.',
        ],
      },
      {
        heading: '7. Stornierung und Nichtabholung',
        paragraphs: [
          'Bereits zubereitete oder in Zubereitung befindliche Bestellungen können in der Regel nicht kostenfrei storniert werden. Nicht abgeholte Bestellungen können dennoch berechnet werden.',
        ],
      },
      {
        heading: '8. Haftung',
        paragraphs: [
          'Für Vorsatz und grobe Fahrlässigkeit haften wir nach den gesetzlichen Bestimmungen. Die Haftung für leichte Fahrlässigkeit ist – soweit gesetzlich zulässig – ausgeschlossen, ausgenommen bei Personenschäden.',
          'Für Inhalte und Leistungen externer Plattformen oder verlinkter Drittanbieter übernehmen wir keine Haftung.',
        ],
      },
      {
        heading: '9. Nutzung der Website',
        paragraphs: [
          'Die Inhalte dieser Website dürfen nur für den privaten, nicht-kommerziellen Gebrauch genutzt werden. Unerlaubte Eingriffe in den Betrieb der Website sowie die missbräuchliche Nutzung von Inhalten sind untersagt.',
        ],
      },
      {
        heading: '10. Anwendbares Recht und Gerichtsstand',
        paragraphs: [
          'Es gilt schweizerisches Recht. Gerichtsstand ist – soweit gesetzlich zulässig – St. Gallen, Schweiz. Zwingende Verbraucherschutzvorschriften bleiben vorbehalten.',
        ],
      },
      {
        heading: '11. Schlussbestimmungen',
        paragraphs: [
          'Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt. An die Stelle der unwirksamen Bestimmung tritt eine wirksame Regelung, die dem wirtschaftlichen Zweck am nächsten kommt.',
          'Wir behalten uns vor, diese AGB jederzeit anzupassen. Massgeblich ist die zum Zeitpunkt des Vertragsschlusses gültige Fassung.',
        ],
      },
    ],
  },
};

export const LEGAL_FOOTER_LINKS: { label: string; to: string }[] = [
  { label: 'Datenschutz', to: '/datenschutz' },
  { label: 'AGB', to: '/agb' },
  { label: 'Impressum', to: '/impressum' },
];
